import {
  UserRound,
  FolderHeart,
  Search,
  Target,
  BarChart3,
  Presentation,
  Palette,
  MessagesSquare,
} from "lucide-react";

/**
 * Der verbindliche Funktionsumfang von MatchGate. Innerhalb einer Zielgruppe
 * gibt es keine Staffelung - alles ist im Abo enthalten.
 *
 * `audiences` legt fest, wer eine Funktion überhaupt bekommt: Pitch-Generator
 * und Media-Kit-Templates sind reine Vereinswerkzeuge. Jede Funktion trägt
 * dazu eine Beschreibung je Blickwinkel, damit „Für Vereine" und „Für
 * Unternehmen" nicht auseinanderlaufen.
 */
export const FEATURES = [
  {
    id: "profile",
    icon: UserRound,
    title: "Profile",
    short: "Vereins- und Unternehmensprofile in einheitlicher Struktur.",
    club: {
      headline: "Ein Profil, das gefunden wird",
      text: "Sportarten, Mitglieder, Reichweite je Kanal, Zielgruppe, Region und verfügbare Sponsoring-Flächen - einmal gepflegt und ab da eure Visitenkarte gegenüber jedem Unternehmen.",
      points: ["Reichweite je Kanal", "Zielgruppe & Region", "Flächen und Pakete"],
    },
    company: {
      headline: "Zeigt, wofür eure Marke steht",
      text: "Branche, Region, Budgetrahmen und die Themen, die zu euch passen. Vereine sehen auf einen Blick, ob eine Anfrage bei euch überhaupt Sinn ergibt.",
      points: ["Branche & Standorte", "Budgetrahmen", "Passende Themen"],
    },
  },
  {
    id: "projekte",
    icon: FolderHeart,
    title: "Projekte",
    short: "Konkrete Vorhaben zusätzlich zum allgemeinen Sponsoring.",
    club: {
      headline: "Fragt gezielt, nicht allgemein",
      text: "Neben dem klassischen Sponsoring könnt ihr, wenn ihr wollt, einzelne Vorhaben ausschreiben: neuer Trikotsatz, Jugendcamp, Zeitmessanlage. Mit Ziel, Budget und Gegenleistung. Freiwillig, aber wirkungsvoll.",
      points: ["Ziel und Budget", "Laufzeit", "Klare Gegenleistung"],
    },
    company: {
      headline: "Unterstützt etwas Greifbares",
      text: "Ein Projekt lässt sich intern und nach außen leichter erklären als ein Betrag. Ihr seht vorher genau, wofür das Geld verwendet wird und was ihr dafür bekommt.",
      points: ["Verwendung sichtbar", "Gegenleistung fixiert", "Gut kommunizierbar"],
    },
  },
  {
    id: "suche",
    icon: Search,
    title: "Suche",
    short: "Filtersuche über Vereine, Projekte und Unternehmen.",
    // Auf beiden Zielgruppenseiten nur kompakt am Ende: Vereine werden in
    // erster Linie gefunden, statt selbst zu suchen - und für Unternehmen
    // erklärt der eigene Abschnitt „Finden" die Filtersuche bereits.
    secondaryFor: ["club", "company"],
    club: {
      headline: "Sucht selbst, statt zu warten",
      text: "Ihr könnt Unternehmen auch selbst nach Branche, Region und Themen filtern - in der Regel führt das Matching euch aber schneller ans Ziel.",
      points: ["Nach Branche filtern", "Nach Region eingrenzen", "Nach Themen suchen"],
    },
    company: {
      headline: "Von tausend Vereinen zu euren drei",
      text: "Filtert nach Region, Sportart, Zielgruppe, Reichweite und Projektart, ausführlich im Abschnitt",
      // Wird als unauffälliger Sprunglink an den Text angehängt.
      ref: { label: "„Vereine finden“", href: "#suche", tail: " weiter oben." },
      points: [
        "Region, Sportart, Zielgruppe",
        "Reichweite und Projektart",
        "Vereine und Projekte parallel",
      ],
    },
  },
  {
    id: "matching",
    icon: Target,
    title: "Matching",
    short: "Übereinstimmung beider Seiten als MatchScore.",
    club: {
      headline: "Eine Zahl, die Zeit spart",
      text: "MatchGate vergleicht eure Angaben mit denen der Unternehmen und errechnet daraus einen MatchScore. Ihr seht sofort, wo sich ein Gespräch wirklich lohnt.",
      points: ["Automatischer Abgleich", "MatchScore je Unternehmen", "Auch je Projekt"],
    },
    company: {
      headline: "Ein Wert, der die Vorauswahl macht",
      text: "Der MatchScore zeigt, wie stark Verein oder Projekt mit euren Kriterien übereinstimmen. Statt jedes Profil einzeln zu prüfen, seht ihr sofort, welche drei sich lohnen.",
      points: ["Übereinstimmung als MatchScore", "Vereine und Projekte", "Nachvollziehbare Kriterien"],
    },
  },
  {
    id: "analytics",
    icon: BarChart3,
    title: "Analytics-Dashboard",
    short: "Sponsorings einrichten, Leistungen belegen, Wirkung auswerten.",
    club: {
      headline: "Das Argument für die Verlängerung",
      text: "Beim Abschluss richtet ihr das Sponsoring gemeinsam mit eurem Partner ein: was vereinbart ist und woran es sich messen lässt. Über die Saison belegt das Dashboard dann eure Leistungen, mit Fotos, Zahlen und Nachweisen. Am Spieltag kostet euch das Sekunden, vieles läuft automatisch.",
      points: [
        "Leistungen belegt",
        "Eintragen in Sekunden",
        "Grundlage für die Verlängerung",
      ],
    },
    company: {
      headline: "Der Kern von MatchGate",
      text: "Jedes Sponsoring wird beim Abschluss eingerichtet: vereinbarte Leistungen, QR-Codes, Links und euer Schwerpunkt. Über die ganze Laufzeit zeigt das Dashboard dann den aktuellen Stand, und bei jeder Zahl steht, woher sie stammt.",
      points: [
        "Leistungen nachweisbar",
        "Herkunft jeder Zahl",
        "Bericht für die Geschäftsführung",
      ],
    },
  },
  {
    id: "pitch",
    icon: Presentation,
    title: "Pitch-Generator",
    short: "Werkzeug für Sponsorenpitches, frei verwendbar.",
    audiences: ["club"],
    club: {
      headline: "Ein Pitch, der nach etwas aussieht",
      text: "Baut euren Sponsorenpitch selbst zusammen und gestaltet ihn ansprechend, ohne Grafikprogramm und ohne durchwachte Nacht vor dem Termin. Der fertige Pitch gehört euch: Ihr könnt ihn genauso im persönlichen Termin zeigen, per Mail verschicken oder ausdrucken, ganz unabhängig von MatchGate.",
      points: [
        "Selbst zusammengestellt",
        "Auch außerhalb der Plattform nutzbar",
        "Als Datei zum Weitergeben",
      ],
    },
  },
  {
    id: "media-kit",
    icon: Palette,
    title: "Media-Kit-Templates",
    short: "Fertige Design-Pakete für den Social-Media-Auftritt.",
    audiences: ["club"],
    club: {
      headline: "Design-Pakete zum Sofort-Einsetzen",
      text: "Fertige Vorlagen für euren Social-Media-Auftritt: Spieltage, neue Partner, Projektupdates. Warum es dabei ist? Beiträge für eure Partner gehören oft zum Sponsoring. Mit den Vorlagen bringt ihr euren Internetauftritt schnell und einfach auf ein neues Level. Ohne Aufpreis im Vereins-Abo.",
      points: ["Fertige Vorlagen", "Partner sichtbar machen", "Ohne Aufpreis im Abo"],
    },
  },
  {
    id: "chat",
    icon: MessagesSquare,
    title: "Chat",
    short: "Integrierter Chat für den Erstkontakt.",
    club: {
      headline: "Erstkontakt ohne Umweg",
      text: "Kein Suchen nach der richtigen Mailadresse: Der erste Kontakt läuft direkt über die Plattform - bei der Person, die dort auch zuständig ist.",
      points: ["Direkt auf der Plattform", "Richtige Ansprechperson", "Alles an einem Ort"],
    },
    company: {
      headline: "Anfragen an einer Stelle",
      text: "Der Erstkontakt läuft über den integrierten Chat, statt in einem Sammelpostfach unterzugehen. Ihr behaltet den Überblick, welches Gespräch wo steht.",
      points: ["Kein Sammelpostfach", "Überblick über Gespräche", "Schnelle Rückmeldung"],
    },
  },
];

/** Ohne `audiences` gilt eine Funktion für beide Seiten. */
const servesAudience = (feature, audience) =>
  (feature.audiences ?? ["club", "company"]).includes(audience);

export const CLUB_FEATURES = FEATURES.filter((f) => servesAudience(f, "club"));
export const COMPANY_FEATURES = FEATURES.filter((f) =>
  servesAudience(f, "company")
);

/** Funktionen, die es nur im Vereins-Abo gibt. */
export const isClubOnly = (feature) => !servesAudience(feature, "company");
